import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultBranch, IBranch } from 'src/Definitions';
import { BranchDto, Client, IBranchDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';

const ColumnSettingsDto = 'BranchDto';

export default class BranchesStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public branches: IBranch[];
    @observable
    public selectedBranch?: IBranch;
    public dataTableStore: DataTableStore<IBranchDto, IBranch>;

    constructor(private globalStore: GlobalStore, private client: Client) {
        this.dataTableStore = new DataTableStore<IBranchDto, IBranch>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllBranches(
                    filter,
                    undefined,
                    page,
                    undefined,
                    orderBy,
                    includeInactives
                ),
            (branch) => this.setupBranches(branch),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedBranch = undefined;
    }

    @action.bound
    public disableBranch(branch: IBranch) {
        this.isLoading = true;
        return this.client
            .archiveBranch(branch.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getColumnSettingsAndBranches() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user!.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public getAllBranches(
        pageNumber?: number,
        pageSize?: number,
        filter?: string,
        regionsFilter?: string
    ) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllBranches(filter, regionsFilter, pageNumber, pageSize, undefined, undefined)
            .then((resp: SwaggerResponse<IBranchDto[]>) =>
                runInAction(() => {
                    this.branches = this.setupBranches(resp.result);
                })
            );
    }

    @action.bound
    public openDialogWithBranch(branch: IBranch) {
        this.isModalOpen = true;
        this.selectedBranch = branch;
    }

    @action.bound
    public recoverBranch(branch: IBranch) {
        this.isLoading = true;
        return this.client
            .reactivateBranch(branch.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState(shouldClearFilter: boolean) {
        if (shouldClearFilter) {
            this.dataTableStore.clearFilter();
        }
        this.dataTableStore.loadData();
        this.closeDialog();
    }

    @action.bound
    public saveBranch(branch: IBranch) {
        this.isLoading = true;
        let promise;
        const branchDto = new BranchDto(branch);
        if (branch.isNew) {
            branchDto.isActive = true;
            promise = this.client.createBranch(branchDto);
        } else {
            promise = this.client.updateBranch(branch.id, branchDto);
        }
        return promise
            .then(() => {
                this.resetUIState(branch.isNew);
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    private setupBranches = (branches: IBranchDto[]) => {
        return branches.map((x) => this.setupBranchMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (branch: IBranchDto): IBranch => {
        return { ...DefaultBranch(), ...branch };
    };

    private setupBranchMenuItems = (branch: IBranch) => {
        branch.menuItems = [];
        branch.menuItems.push({
            name: 'Edit',
            onClick: () => this.openDialogWithBranch(branch),
        });
        if (branch.isActive) {
            branch.menuItems.push({
                isConfirm: true,
                name: 'Disable',
                onClick: () => this.disableBranch(branch),
            });
        } else {
            branch.menuItems.push({
                isConfirm: true,
                name: 'Recover',
                onClick: () => this.recoverBranch(branch),
            });
        }
        return branch;
    };
}



// WEBPACK FOOTER //
// ./src/stores/BranchesStore/BranchesStore.ts